import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

export const overviewPanelStyles: Record<string, SxProps<Theme>> = {
  root: (theme) => ({
    width: { xs: '100%', md: 360 },
    flexShrink: 0,
    height: '100%',
    overflowY: 'auto',
    borderRight: { md: `1px solid ${theme.palette.divider}` },
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  }),
  header: { display: 'flex', flexDirection: 'column', gap: 0.5 },
  // Figma: Manrope ExtraBold 16 / 136.6%, 0 letter-spacing, text-tertiary.
  title: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '1rem',
    lineHeight: 1.366,
    letterSpacing: 0,
    color: theme.palette.surface.legalText,
    margin: 0,
  }),
  // Figma: Manrope Regular 14 / 136.6%, 0 letter-spacing, text-tertiary.
  subtitle: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '0.875rem',
    lineHeight: 1.366,
    letterSpacing: 0,
    color: theme.palette.surface.legalText,
  }),
  metrics: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': { borderBottom: `1px solid ${theme.palette.divider}` },
  }),
  metricRow: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    padding: theme.spacing(1),
  }),
  metricRowTop: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  }),
  metricNote: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  }),
  metricLabel: { display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 },
  // Figma: Manrope Medium 14 / 136.6%, 0 letter-spacing.
  metricLabelText: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: '0.875rem',
    lineHeight: 1.366,
    letterSpacing: 0,
    color: theme.palette.text.primary,
  }),
  // Figma: Manrope ExtraBold 14 / 136.6%, color #202599 (distinct from
  // primary.main) — uniform across every metric row, emphasized or not.
  metricValue: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.875rem',
    lineHeight: 1.366,
    color: theme.palette.surface.dataValue,
    whiteSpace: 'nowrap',
  }),
  metricValueEmphasis: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.875rem',
    lineHeight: 1.366,
    color: theme.palette.surface.dataValue,
    whiteSpace: 'nowrap',
  }),
  infoIcon: (theme) => ({
    fontSize: 15,
    color: theme.palette.surface.placeholder,
    outline: 'none',
    // `alignSelf` centers it in a `baseline`-aligned flex row (e.g. the
    // opportunity-score row's big number + unit label); `verticalAlign`
    // centers it against the surrounding line when it's inline text
    // instead (e.g. the Projections Card's "Each child receives" line).
    alignSelf: 'center',
    verticalAlign: 'middle',
  }),
  // MUI's default tooltip bubble is translucent (rgba grey); pin it to a
  // fully opaque, on-brand navy instead.
  tooltipPopup: (theme) => ({
    backgroundColor: theme.palette.brand.navy.deep,
    opacity: 1,
  }),
  tooltipArrow: (theme) => ({
    color: theme.palette.brand.navy.deep,
  }),
  tooltipBody: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    maxWidth: 260,
    padding: theme.spacing(0.5),
  }),
  tooltipHint: {
    fontWeight: 700,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
  },
  tooltipDescription: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
  },
  // Compact variant — a single centered paragraph in a narrower box, no
  // hint/source rows (e.g. the Projections Card's "Each child receives"
  // tooltip), per Figma: Manrope Medium 12px, centered, 198px wide.
  tooltipBodyCompact: {
    width: 198,
    padding: 0,
    textAlign: 'center',
  },
  tooltipDescriptionCompact: {
    fontWeight: 500,
    fontSize: '0.75rem',
    lineHeight: 1.366,
    letterSpacing: 0,
    textAlign: 'center',
  },
  tooltipSource: (theme) => ({
    fontSize: '0.75rem',
    lineHeight: 1.35,
    fontStyle: 'italic',
    opacity: 0.8,
    borderTop: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    paddingTop: theme.spacing(0.5),
  }),
  needle: (theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), padding: theme.spacing(1) }),
  needleHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 },
  needleLabel: (theme) => ({
    // Figma: Manrope ExtraBold 12 / 100%, 0 letter-spacing.
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.75rem',
    lineHeight: 1,
    letterSpacing: 0,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  }),
  needleBar: (theme) => ({
    height: 6,
    borderRadius: 999,
    backgroundColor: theme.palette.divider,
    '& .MuiLinearProgress-bar': {
      borderRadius: 999,
      backgroundColor: theme.palette.secondary.main,
    },
  }),
  divider: { my: 1 },
  selectedHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  // Figma: Manrope ExtraBold 12 / 150%, 0 letter-spacing, text-tertiary.
  selectedCount: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    letterSpacing: 0,
    color: theme.palette.surface.legalText,
  }),
  clearAll: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.8125rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
  }),
  emptyHint: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  }),
  statusBox: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingBlock: theme.spacing(2),
  }),
  errorText: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    color: theme.palette.error.main,
  }),
  retryLink: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.8125rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    width: 'fit-content',
  }),
  areaList: (theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }),
  areaCard: (theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.25) }),
  areaCardHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  // Figma: Manrope ExtraBold 14 / 136.6%, 0 letter-spacing.
  areaName: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.875rem',
    lineHeight: 1.366,
    letterSpacing: 0,
    color: theme.palette.text.primary,
    margin: 0,
  }),
  // Figma: Manrope Medium 12 / 150%, text-tertiary.
  areaType: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.surface.legalText,
  }),
  removeButton: (theme) => ({ color: theme.palette.text.secondary, marginTop: -0.5 }),
  scoreRow: { display: 'flex', alignItems: 'baseline', gap: 0.75 },
  // Groups the label + info icon on their own so the icon centers to *this*
  // small text's line height, not the row's — the row's own cross-size is
  // set by the much-taller score number, and centering against that put
  // the icon noticeably above the label instead of level with it.
  scoreLabelGroup: { display: 'inline-flex', alignItems: 'center', gap: 0.375 },
  // Figma: Manrope ExtraBold 24 / 136.6%, color #202599.
  scoreValue: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '1.5rem',
    lineHeight: 1.366,
    color: theme.palette.surface.dataValue,
  }),
  // Figma: Manrope Regular 12 / 150%, color text.primary.
  scoreLabel: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  }),
  scoreBar: (theme) => ({
    height: 6,
    borderRadius: 999,
    backgroundColor: theme.palette.divider,
    '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: theme.palette.primary.main },
  }),
  areaGrid: (theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.25),
    paddingTop: theme.spacing(0.5),
  }),
  // Figma: Manrope Regular 12 / 136.6%, letter-spacing -0.132, color #737373.
  areaStatLabel: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    lineHeight: 1.366,
    letterSpacing: '-0.132px',
    color: theme.palette.surface.footerText,
  }),
  // Figma: Manrope ExtraBold 14 / 150%, letter-spacing -0.154, color #202599.
  areaStatValue: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    letterSpacing: '-0.154px',
    color: theme.palette.surface.dataValue,
  }),
  areaStatNote: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.6875rem',
    color: theme.palette.text.secondary,
  }),
};
